import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, LayoutGrid, X } from "lucide-react";
import {
  buildCategoryTree,
  collectAncestorIds,
  findCategoryPath,
  pruneShopCategoryTree,
  type CatalogCategoryFlat,
  type CatalogCategoryNode,
} from "../../lib/catalogCategories";

type CategoryFilterProps = {
  categories: CatalogCategoryFlat[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** When true, hide branches with no shop-listed products in subtree. */
  shopProductFilter?: boolean;
};

/** Mobile sticky bar: [category btn][search][sort] + optional breadcrumb row. */
export function CategoryFilterMobileControls({
  categories,
  selectedId,
  onSelect,
  shopProductFilter = false,
  search,
  sort,
}: CategoryFilterProps & { search: ReactNode; sort: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-w-0 max-w-full space-y-2">
      <div className="flex min-w-0 max-w-full items-stretch gap-2">
        <CategoryFilterMobileTrigger
          active={Boolean(selectedId)}
          onClick={() => setOpen(true)}
        />
        <div className="min-w-0 flex-1 overflow-hidden">{search}</div>
        <div className="shrink-0">{sort}</div>
      </div>
      <CategoryFilterBreadcrumbs categories={categories} selectedId={selectedId} onSelect={onSelect} />
      <CategoryPickerDrawer
        open={open}
        onClose={() => setOpen(false)}
        categories={categories}
        selectedId={selectedId}
        shopProductFilter={shopProductFilter}
        onSelect={(id) => {
          onSelect(id);
          setOpen(false);
        }}
      />
    </div>
  );
}

function CategoryFilterMobileTrigger({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Browse categories"
      className={`relative flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl border bg-surface transition-colors active:bg-white/5 ${
        active ? "border-brand-pink/50 text-brand-pink" : "border-white/10 text-white/60"
      }`}
    >
      <LayoutGrid size={18} strokeWidth={2} />
      {active ? (
        <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand-pink" aria-hidden />
      ) : null}
    </button>
  );
}

export function CategoryFilterBreadcrumbs({
  categories,
  selectedId,
  onSelect,
}: Pick<CategoryFilterProps, "categories" | "selectedId" | "onSelect">) {
  const path = useMemo(() => findCategoryPath(categories, selectedId), [categories, selectedId]);
  if (!path.length) return null;

  return (
    <nav
      aria-label="Category breadcrumb"
      className="scroll-row min-w-0 items-center gap-0.5 pb-0.5 text-[11px]"
    >
      <button
        type="button"
        onClick={() => onSelect(null)}
        className="shrink-0 rounded-md px-1 py-0.5 text-white/45 transition-colors active:bg-white/5"
      >
        All
      </button>
      {path.map((segment, index) => {
        const isLast = index === path.length - 1;
        return (
          <span key={segment.id} className="flex shrink-0 items-center gap-0.5">
            <ChevronRight size={12} className="text-white/25" aria-hidden />
            <button
              type="button"
              onClick={() => onSelect(segment.id)}
              className={`max-w-[9rem] truncate rounded-md px-1 py-0.5 transition-colors active:bg-white/5 sm:max-w-[12rem] ${
                isLast ? "font-medium text-white/85" : "text-white/55"
              }`}
            >
              {segment.name}
            </button>
          </span>
        );
      })}
    </nav>
  );
}

export function CategoryFilterSidebar({
  categories,
  selectedId,
  onSelect,
  shopProductFilter = false,
}: CategoryFilterProps) {
  return (
    <CategoryTreeList
      categories={categories}
      selectedId={selectedId}
      onSelect={onSelect}
      shopProductFilter={shopProductFilter}
    />
  );
}

function CategoryTreeList({
  categories,
  selectedId,
  onSelect,
  shopProductFilter,
  touchFriendly = false,
}: CategoryFilterProps & { touchFriendly?: boolean }) {
  const tree = useMemo(() => {
    const built = buildCategoryTree(categories);
    return shopProductFilter ? pruneShopCategoryTree(built) : built;
  }, [categories, shopProductFilter]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const id of collectAncestorIds(categories, selectedId)) next.add(id);
      if (selectedId) next.add(selectedId);
      return next;
    });
  }, [categories, selectedId]);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-1">
      <CategoryRow label="All" active={!selectedId} onSelect={() => onSelect(null)} />
      {tree.map((node) => (
        <CategoryTreeBranch
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          expanded={expanded}
          onToggleExpand={toggleExpand}
          onSelect={(id) => onSelect(id)}
          touchFriendly={touchFriendly}
        />
      ))}
    </div>
  );
}

function CategoryPickerDrawer({
  open,
  onClose,
  categories,
  selectedId,
  onSelect,
  shopProductFilter,
}: CategoryFilterProps & { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            type="button"
            aria-label="Close category picker"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10050] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="category-drawer-title"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 380 }}
            className="fixed bottom-0 left-0 top-0 z-[10051] flex w-[min(88vw,320px)] max-w-full flex-col border-r border-white/10 bg-brand-dark shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
              <p id="category-drawer-title" className="font-display text-sm font-semibold">
                Categories
              </p>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1.5 text-white/50 transition-colors hover:bg-white/5 hover:text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <CategoryTreeList
                categories={categories}
                selectedId={selectedId}
                onSelect={onSelect}
                shopProductFilter={shopProductFilter}
                touchFriendly
              />
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

function CategoryTreeBranch({
  node,
  depth,
  selectedId,
  expanded,
  onToggleExpand,
  onSelect,
  touchFriendly = false,
}: {
  node: CatalogCategoryNode;
  depth: number;
  selectedId: string | null;
  expanded: Set<string>;
  onToggleExpand: (id: string) => void;
  onSelect: (id: string) => void;
  touchFriendly?: boolean;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const py = touchFriendly ? "py-3" : "py-2";

  return (
    <div>
      <div className="flex min-w-0 items-stretch" style={{ paddingLeft: depth * 12 }}>
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
            onClick={() => onToggleExpand(node.id)}
            className={`flex shrink-0 items-center justify-center rounded-lg text-white/40 transition-colors hover:bg-white/5 hover:text-white/70 ${touchFriendly ? "w-10" : "w-8"}`}
          >
            {isOpen ? (
              <ChevronRight size={16} className="rotate-90" />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        ) : (
          <span className={touchFriendly ? "w-10 shrink-0" : "w-8 shrink-0"} />
        )}
        <CategoryRow
          label={node.name}
          active={selectedId === node.id}
          onSelect={() => onSelect(node.id)}
          className={`flex-1 ${py}`}
        />
      </div>
      {hasChildren && isOpen
        ? node.children.map((child) => (
            <CategoryTreeBranch
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
              touchFriendly={touchFriendly}
            />
          ))
        : null}
    </div>
  );
}

function CategoryRow({
  label,
  active,
  onSelect,
  className = "",
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`block w-full rounded-lg px-3 text-left text-xs transition-colors ${className} ${
        active ? "bg-white/10 font-semibold text-white" : "text-white/50 hover:bg-white/5 hover:text-white/80"
      }`}
    >
      {label}
    </button>
  );
}
